import { ConnectionDetails, Dayts, QueryInputType, queryInput } from '@/utils';
import {
  DtrLocation,
  ManifestHistoryWithRoute,
  ManifestHistoryWithRouteSearch,
  ManifestHistoryWithRouteStatusEnum,
  PotentialLocation,
  QueryHistory,
  QueryHistoryStatusEnum,
  SortDirectionEnum,
} from '@/models/gen/graphql';
import { NormalizedLocation, getDtrNormalizedAddress } from '@/api/services/locations/getNormalizedAddress';

import createDtrLocationBulk from './createDtrLocationBulk';
import { getDtrLocation } from '@/features/DriveTimeTool/api/services/searchDtrLocation';
import searchManifestHistoryWithRoute from './searchManifestHistoryWithRoute';
import searchQueryHistory from './searchQueryHistory';

export const normalizeDtrLocation = async (address: string, name?: string, airportCode?: string): Promise<DtrLocation> => {
  const normalized = await getDtrNormalizedAddress(address);
  // we only search for locations that have an address
  if (normalized?.address) {
    const dtrLocation = await getDtrLocation(normalized, airportCode);
    if (dtrLocation) return dtrLocation;
  }
  const [location] = await createDtrLocationBulk([
    {
      ...converNormalizedAddressToDtrLocation(normalized),
      name: name || normalized.formattedAddress,
      airportCode: airportCode,
    },
  ]);
  return location;
};

const converNormalizedAddressToDtrLocation = (
  input: NormalizedLocation
): Pick<DtrLocation, 'address' | 'cityName' | 'coordinates' | 'countryCode' | 'stateCode' | 'zipCode'> => ({
  address: input.address || input.formattedAddress.split(',')[0],
  cityName: input.cityName,
  coordinates: { lat: input.coordinates?.lat, lng: input.coordinates?.lng },
  countryCode: input.countryCode,
  stateCode: input.stateCode,
  zipCode: input.zipCode,
});

export const convertDtrLocationToPotentialLocation = (location: DtrLocation): PotentialLocation => {
  if (!location) return null;
  return {
    dtr_location_id: location.id,
    name: location.name,
    address: location.address,
    cityName: location.cityName,
    coordinates: { lat: location.coordinates?.lat, lng: location.coordinates?.lng },
    countryCode: location.countryCode,
    stateCode: location.stateCode,
    zipCode: location.zipCode,
  };
};

export type Route = {
  id: string;
  manifestHistoryId: string;
  puLocationId: string;
  doLocationId: string;
  startDate?: string;
  endDate?: string;
  interval?: string;
  status: RouteStatusEnum;
  createdAt: string;
  routeId?: string;
};
export enum RouteStatusEnum {
  InQueue = 'IN_QUEUE',
  Running = 'RUNNING',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
}
export type ManifestWithRoutes = {
  manifestHistoryId?: string;
  importId?: string;
  filename?: string;
  createdAt: string;
  routes: Route[];
};

export type GetHistoryInput = {
  startDate: string;
  endDate: string;
  puLocationId: string[];
  doLocationId: string[];
  type: 'import' | 'report';
};
export const getHistory = async (filters: GetHistoryInput): Promise<ManifestWithRoutes[]> => {
  const query: Pick<ManifestHistoryWithRouteSearch, 'createdAt' | 'puLocationId' | 'doLocationId' | 'manifestHistoryId'> = {
    createdAt: queryInput(
      [
        new Dayts(new Dayts(filters.startDate).startOf('day')).formatAs(Dayts.Format.DATETIME_RFC),
        new Dayts(new Dayts(filters.endDate).endOf('day')).formatAs(Dayts.Format.DATETIME_RFC),
      ],
      QueryInputType.RANGE,
      SortDirectionEnum.Desc,
      0
    ),
    puLocationId: queryInput(filters.puLocationId),
    doLocationId: queryInput(filters.doLocationId),
  };
  // query history
  let routeHistoryRes: ConnectionDetails<QueryHistory> = null;
  if (!filters.type || filters.type === 'report') routeHistoryRes = await searchQueryHistory([query], { pageSize: null });
  let manifestHistoryRes: ConnectionDetails<ManifestHistoryWithRoute> = null;
  if (!filters.type || filters.type === 'import') manifestHistoryRes = await searchManifestHistoryWithRoute([query], { pageSize: null });
  // convert to routes
  const routeHistory = (routeHistoryRes?.rows || []).map((route) => convertQueryHistoryToManifestWithRoutes(route));
  const manifestHistoryGrouped = (manifestHistoryRes?.rows || []).reduce<Record<string, ManifestWithRoutes>>((acc, row) => {
    const key = row.manifestHistoryId;
    if (!acc[key]) {
      acc[key] = convertManifestHistoryWithRouteToManifestWithRoutes(row);
      return acc;
    }
    acc[key].routes.push(convertManifestHistoryWithRouteToRoute(row));
    return acc;
  }, {});
  // merge routes
  const mergedHistory = [...routeHistory, ...Object.values(manifestHistoryGrouped)].sort((a, b) =>
    new Dayts(b.createdAt).diff(a.createdAt)
  );
  return mergedHistory;
};

const convertQueryHistoryToManifestWithRoutes = (input: QueryHistory): ManifestWithRoutes => {
  return {
    manifestHistoryId: null,
    importId: null,
    createdAt: input.createdAt,
    routes: [convertQueryHistoryToRoute(input)],
  };
};
const convertQueryHistoryToRoute = (input: QueryHistory): Route => {
  return {
    id: input.id,
    manifestHistoryId: null,
    puLocationId: input.puLocationId,
    doLocationId: input.doLocationId,
    startDate: input.startDate,
    endDate: input.endDate,
    interval: input.interval,
    status: convertQueryHistoryEnumToRouteStatusEnum(input.status) || null,
    createdAt: input.createdAt,
  };
};
const convertQueryHistoryEnumToRouteStatusEnum = (input: QueryHistoryStatusEnum): RouteStatusEnum => {
  switch (input) {
    case QueryHistoryStatusEnum.Completed:
      return RouteStatusEnum.Completed;
    case QueryHistoryStatusEnum.Running:
      return RouteStatusEnum.Running;
    case QueryHistoryStatusEnum.Failed:
      return RouteStatusEnum.Failed;
    case QueryHistoryStatusEnum.InQueue:
      return RouteStatusEnum.InQueue;
    default:
      return null;
  }
};
const convertManifestHistoryWithRouteToManifestWithRoutes = (input: ManifestHistoryWithRoute): ManifestWithRoutes => {
  return {
    manifestHistoryId: input.manifestHistoryId,
    importId: input.manifestImportId,
    filename: input.filename,
    createdAt: input.createdAt,
    routes: [convertManifestHistoryWithRouteToRoute(input)],
  };
};
const convertManifestHistoryWithRouteToRoute = (input: ManifestHistoryWithRoute): Route => {
  return {
    id: input.routeId,
    manifestHistoryId: input.manifestHistoryId,
    puLocationId: input.puLocationId,
    doLocationId: input.doLocationId,
    startDate: null,
    endDate: null,
    interval: null,
    status: convertManifestHistoryWithRouteStatusEnumToRouteStatusEnum(input.status) || null,
    createdAt: input.createdAt,
    routeId: input.routeId,
  };
};
const convertManifestHistoryWithRouteStatusEnumToRouteStatusEnum = (input: ManifestHistoryWithRouteStatusEnum): RouteStatusEnum => {
  switch (input) {
    case ManifestHistoryWithRouteStatusEnum.Completed:
      return RouteStatusEnum.Completed;
    case ManifestHistoryWithRouteStatusEnum.Running:
      return RouteStatusEnum.Running;
    case ManifestHistoryWithRouteStatusEnum.Failed:
      return RouteStatusEnum.Failed;
    case ManifestHistoryWithRouteStatusEnum.InQueue:
      return RouteStatusEnum.InQueue;
    default:
      return null;
  }
};
